import { useCallback, useEffect, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { useDispatch, useSelector } from 'react-redux'

import { shouldDisplayOfflineTransactionHeader } from '../selectors/order'
import { getIsKiosk } from '../selectors/menus'
import { getActiveAttendantId } from '../selectors/attendant'

import networkConnected from '../utils/networkConnected'
import { ToastManager } from '../utils/toastManager'

import { setRoute } from '../VNRoutes/ActionCreators'
import { setShouldStartSync } from '../VNSyncManager/ActionCreators'

const THIRTY_MINS = 30 * 60 * 1000 // 30 minutes in milliseconds

/**
 * This hook is used to automatically replay offline orders after 30 minutes of inactivity.
 *
 * @link https://idletimer.dev/docs/features/idle-detection - package for idle detection
 */
const useIdleReplay = () => {
  let idleTimer = null

  const [shouldReplay, setShouldReplay] = useState(false)
  const hasOfflineTransactions = useSelector(shouldDisplayOfflineTransactionHeader)
  const isKiosk = useSelector(getIsKiosk)
  const currentAttendantId = useSelector(getActiveAttendantId)

  const dispatch = useDispatch()

  const executeReplay = useCallback(async () => {
    if (!hasOfflineTransactions) {
      ToastManager.info('No offline orders to auto-sync.', { autoClose: 2000 })
      return
    }

    // only attempt idle sync if network is connected
    try {
      await networkConnected()
    } catch {
      return
    }

    if (!isKiosk && currentAttendantId) {
      dispatch(setRoute('/orders/device-summary?from=idleReplay'))
    }

    const bridgeUnavailableCallback = () => {
      ToastManager.error('Unable to connect to peripheral. Please try again later.')
    }

    // replay orders with offline credit card approvals
    dispatch(setShouldStartSync(true, bridgeUnavailableCallback))

    // reset idle timer
    if (idleTimer) {
      idleTimer.reset()
      setShouldReplay(false)
    }
  }, [dispatch, idleTimer, isKiosk, hasOfflineTransactions, currentAttendantId])

  // After 30 minutes of inactivity
  const handleOnIdle = () => {
    setShouldReplay(true)
  }

  // When the user begins interacting again after 30 minutes has passed
  const handleOnActive = () => {
    setShouldReplay(false)
  }

  useEffect(() => {
    if (shouldReplay) {
      executeReplay()
    }
  }, [executeReplay, shouldReplay])

  // Idle Timer Setup
  idleTimer = useIdleTimer({
    timeout: THIRTY_MINS,
    onIdle: handleOnIdle,
    onActive: handleOnActive,
    debounce: 500,
  })
}

export default useIdleReplay
